import type { Invoice, Subscription } from '../types/subscriptionManagement'

/** Statuses that must not affect effective renewal. */
const NON_PAID_STATUSES = new Set([
  'unpaid',
  'overdue',
  'cancelled',
  'draft',
])

/** Parse YYYY-MM-DD in local calendar (invalid → null). */
export function parseYmdLocal(s: string | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || '').trim())
  if (!m) return null
  const y = Number(m[1])
  const mo = Number(m[2])
  const d = Number(m[3])
  const dt = new Date(y, mo - 1, d)
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return null
  }
  return dt
}

export function formatYmdLocal(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

/** Only **Paid** invoices contribute to effective renewal. */
export function isPaidInvoiceStatus(status: string | undefined): boolean {
  const s = String(status || '').trim().toLowerCase()
  if (!s || NON_PAID_STATUSES.has(s)) return false
  return s === 'paid'
}

function daysInMonth(y: number, monthIndex: number): number {
  return new Date(y, monthIndex + 1, 0).getDate()
}

/** Add whole months keeping anchor day (clamped to target month length). */
function addMonthsAnchored(base: Date, monthsToAdd: number): Date {
  const anchorDay = base.getDate()
  const t = new Date(base.getFullYear(), base.getMonth() + monthsToAdd, 1)
  const dim = daysInMonth(t.getFullYear(), t.getMonth())
  t.setDate(Math.min(anchorDay, dim))
  return t
}

/**
 * Coverage end from coverage start + billing cycle.
 * Monthly +1 month, Quarterly +3 months, Annual +12 months.
 */
export function coverageEndFromStart(
  start: Date,
  billingCycle: string,
): Date {
  const c = String(billingCycle || 'Monthly').trim().toLowerCase()
  if (c === 'annual') return addMonthsAnchored(start, 12)
  if (c === 'quarterly') return addMonthsAnchored(start, 3)
  return addMonthsAnchored(start, 1)
}

export interface CoverageInput {
  invoiceDate: string
}

export interface CoverageSubInput {
  billingCycle: string
}

export interface GeneratedCoverage {
  subscriptionStartDate: string
  subscriptionEndDate: string
}

/**
 * Derive invoice coverage from invoice date + subscription billing cycle.
 * Start = invoice date; end = start + one billing period.
 */
export function generateInvoiceCoverage(
  inv: CoverageInput,
  sub: CoverageSubInput,
): GeneratedCoverage | null {
  const start = parseYmdLocal(inv.invoiceDate)
  if (!start) return null
  const end = coverageEndFromStart(start, sub.billingCycle)
  return {
    subscriptionStartDate: formatYmdLocal(start),
    subscriptionEndDate: formatYmdLocal(end),
  }
}

type InvoiceLike = Pick<
  Invoice,
  'invoiceDate' | 'subscriptionStartDate' | 'subscriptionEndDate'
>

/**
 * Backfill empty coverage fields on load/save when invoice date + billing cycle exist.
 * Does not overwrite user-entered dates.
 */
export function enrichInvoiceCoverageFields<T extends InvoiceLike>(
  inv: T,
  sub?: Pick<Subscription, 'billingCycle'> | null,
): T {
  if (!sub?.billingCycle || !inv.invoiceDate) return inv
  const generated = generateInvoiceCoverage(
    { invoiceDate: inv.invoiceDate },
    { billingCycle: sub.billingCycle },
  )
  if (!generated) return inv

  const out = { ...inv }
  if (!String(out.subscriptionStartDate || '').trim()) {
    out.subscriptionStartDate = generated.subscriptionStartDate
  }
  if (!String(out.subscriptionEndDate || '').trim()) {
    out.subscriptionEndDate = generated.subscriptionEndDate
  }
  return out
}
